import React, { useState, useEffect } from "react";
import ButtonModal from "../../stories/Modals/ButtonModal";
import { Navigate, useNavigate } from "react-router";
import SearchForm from "../../stories/Forms/SearchForm";
import {
  patientRecordsFields,
  links,
  clinicalStudiesFields,
  vhPatientRecordsFields,
  vhClinicalStudiesFields,
  getCrioLabel,
} from "../../stories/Forms/fields";
import useApis from "../../services/useAPI";
import { useAuth } from "../../components/AuthContext/AuthContext";
import formatData, {
  formatStudyName,
  formatTestType,
  formatFullName,
  removeCountryCode,
} from "../../utils/utils";
import {
  searchBookingsUrl,
  checkClinicalPatientUrl,
  patientSearchApi,
} from "../../utils/constants";
import { dateValidationSchema } from "../../stories/Forms/validationSchema";
import logo from "../../assets/images/boots-myhealthchecked-logo.svg";
import img from "../../assets/images/notification.svg";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import { XCircleFill } from "react-bootstrap-icons";
import PageLayout from "../../components/Layout/PageLayout";
import VitalityDuplicatedTable from "../../stories/Tables/VitalityDuplicatedTable";
import { Primary } from "../../stories/Buttons/Button.stories";
import VHConfirmationPopup from "../../stories/Modals/VHConfirmationPopup";

function PatientRecord() {
  // States for managing MFA, modals, and view states
  const auth = useAuth();
  const [mfa] = useState(
    JSON.parse(localStorage.getItem("mfa_enabled")) || false,
  );
  const [show, setShow] = useState(!mfa);
  const [data, setData] = useState(false);
  const [fields, setFields] = useState(clinicalStudiesFields);
  let selectedService = localStorage.getItem("selectedService");
    const isVhlogin = auth?.featureFlag;
  useEffect(() => {
    const isHealthCare = selectedService === "healthCare";
    const fields = isVhlogin
      ? isHealthCare
        ? vhPatientRecordsFields
        : vhClinicalStudiesFields
      : isHealthCare
        ? patientRecordsFields
        : clinicalStudiesFields;
    setFields(
      fields.map((field) =>
        field.name === "crio_identifier"
          ? { ...field, label: getCrioLabel(isVhlogin) }
          : field,
      ),
    );
  }, [selectedService, isVhlogin]);
  // Hook for navigation
  const navigate = useNavigate();
  const [isDuplicateSelection, setIsDuplicateSelection] = useState(false);
  const [formError, setFormError] = useState("");
  const vhColumns = [
    { dataField: "bookingId", text: "Consultation ID" },
    { dataField: "first_name", text: "First name" },
    { dataField: "last_name", text: "Last name" },
    { dataField: "dob", text: "Date of birth" },
    { dataField: "email", text: "Email address" },
    { dataField: "status", text: "Action" },
  ];

  const {
    callApi,
    resultPost,
    serverError,
    tokenError,
    toggleShowError,
    showError,
    setServerError,
    setResultPost
  } = useApis();



  const buildVhRows = (users = []) =>
    users.map((user) => ({
      bookingId: user.booking_id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      dob: user.dob,
      status: {
        component: (
          <Primary hover={true} label="View" size="small" variant="primary" />
        ),
      },
    }));

  const url = isVhlogin ? patientSearchApi : searchBookingsUrl;

  const handleSubmitDuplicateId = async (data) => {
    if (selectedService === "clinical") {
      const { status, ...cleanData } = data;
      return (
        <Navigate
          state={{
            data: cleanData,
            user: resultPost?.user_info,
          }}
          to={"/patient-record/select-patient"}
        />
      );
    } else {
      const { status, dob, ...dataWithoutStatus } = data;
      setData(dataWithoutStatus);
      await callApi(dataWithoutStatus, url, "POST", {
        Authorization: `Bearer ${
          localStorage.getItem("access_token") || auth.accessToken
        }`,
        "Content-Type": "application/json",
      });
    }
  };

  const vhPayload = resultPost?.data ?? resultPost;

  const vhUsers = Array.isArray(vhPayload?.users) ? vhPayload.users : [];

  const showVhDuplicateTable =
    isVhlogin && !serverError && !!vhPayload?.duplicate;

  useEffect(() => {
    if ((serverError || tokenError) && !isVhlogin) {
      auth?.showToast("Error", tokenError || serverError, "../assets/mhc.png", {
        icon: <XCircleFill />,
      });
    }
  }, [serverError, tokenError]);

  // Handle hiding the MFA modal
  const handleHide = () => {
    localStorage.setItem("defered_mfa", true);
    setShow(false);
  };

  // Set the page title using the authentication context
  useEffect(() => {
    if (auth && selectedService === "healthCare") {
      auth.setTitle("Healthcare Services");
    } else {
      auth.setTitle("Clinical studies");
    }
  }, [auth?.title]);

  useEffect(() => {
    auth?.resetAllowedStage();
    localStorage.setItem("patientCard", "false");
  }, []);

  // Navigate to enable OTP page
  const handleMFA = async () => {
    navigate("/welcome");
  };
  /**
   * Handles form submission for searching patient records.
   */
  const handleSubmit = async (data) => {
    setFormError("");
    setServerError("")
    setIsDuplicateSelection(false);
    let url = searchBookingsUrl;
    if (selectedService === "clinical") {
      url = checkClinicalPatientUrl;
    }
    if (isVhlogin && selectedService === "healthCare") {
      url = patientSearchApi;
    }
    const formattedData = {
      ...formatData(data),
      first_name: formatFullName(data.first_name),
      last_name: formatFullName(data.last_name),
      date_of_birth: data.dob || data.date_of_birth,
      study: formatStudyName(data.study),
      test_type: formatTestType(data.test_type),
      service_type: selectedService,
      phone_number_primary: removeCountryCode(data.phone_number_primary),
    };
    sessionStorage.setItem("clinicalPatientDetails", JSON.stringify(formattedData));
    auth.setClinicalPatientDetails(formattedData);
    setData(formattedData);
    await callApi(formattedData, url, "POST", {
      Authorization: `Bearer ${
        localStorage.getItem("access_token") || auth.accessToken
      }`,
      "Content-Type": "application/json",
    });
    auth.allowStage("patient-record");
  };

  const handleFormError = (error) => {
    setFormError(error);

    if (error) {
      setResultPost(null);
      setServerError("");
    }
  };

  /**
   * Flattens a nested object into a single-level object with dot-separated keys.
   */
  const flattenObject = (obj, parentKey = "", res = {}) => {
    for (let key in obj) {
      if (
        obj[key] &&
        typeof obj[key] === "object" &&
        !Array.isArray(obj[key])
      ) {
        flattenObject(obj[key], `${parentKey}${key}.`, res);
      } else {
        res[`${parentKey}${key}`] = obj[key];
      }
    }
    return res;
  };

  // Helper function to determine if there are multiple results
  const hasMultipleResults = (resultPost, selectedService) => {
    return (
      resultPost?.duplicate ||
      (selectedService === "clinical" && resultPost.duplicate)
    );
  };

  // Helper function to render navigation based on result
  const renderNavigationForResult = (resultPost, data, selectedService) => {
    if (hasMultipleResults(resultPost, selectedService)) {
      return (
        <Navigate
          to="/patient-record/user-details"
          state={{
            data: data,
            user: resultPost?.user_info,
            isDuplicate: "true",
            id: resultPost.bookings?.[0]?.phlebotomist_booking_id,
            serviceType: selectedService,
          }}
        />
      );
    }

    if (!resultPost.duplicate || resultPost.count <= 1) {
      return (
        <Navigate
          state={{
            data: data,
            user: resultPost?.user_info,
            id: resultPost.bookings?.[0]?.phlebotomist_booking_id,
            ...(isDuplicateSelection && { resultPost, isDublicated: true }),
          }}
          to={getNavigationPath(resultPost, selectedService)}
        />
      );
    }

    return null;
  };

  // Helper function to determine navigation path
  const getNavigationPath = (resultPost, selectedService) => {
    const basePath = "/patient-record/";

    if (resultPost?.success === false && selectedService === "clinical") {
      return `${basePath}clinical-details-not-found`;
    }

    return selectedService === "healthCare"
      ? `${basePath}patient-details`
      : `${basePath}select-patient`;
  };

    // Helper function to get form error message
  const getFormErrorMessage = (selectedService) => {
    return selectedService === "healthCare"
      ? isVhlogin ? "Please enter at least 3 fields to find registered patient.":"Please fill in at least 3 fields to submit the form"
      : "Please complete all fields";
  };

  const updatedServerError =
  serverError === "No bookings found for the provided search criteria." && isVhlogin
    ? "No match found! Please verify the details and try again. Please ensure that patient has completed registration in myhealthchecked.com. Please search using MyHealthChecked registration details only."
    : serverError;

  // Helper function to render the search form section
  const renderSearchForm = ({ mfaProps, searchFormProps, toasterProps }) => {
    const { mfa, show, handleMFA, handleHide } = mfaProps;
    const {
      fields,
      selectedService,
      handleSubmit,
      dateValidationSchema,
      serverError,
      tokenError,
    } = searchFormProps;
    const { showError, toggleShowError } = toasterProps;

    return (
      <>
        {/* change request for MFA disabled  */}
        {/* {!mfa && !localStorage.getItem("defered_mfa") && (
          <ButtonModal
            heading="Enable MFA"
            message="Multi-factor Authentication (also known as Two Factor Authentication or 2FA) improves your account security by requiring a second means of authentication when logging in."
            onClick={handleMFA}
            onHide={handleHide}
            show={show}
          />
        )} */}
        {isVhlogin ? (
          <PageLayout
            heading={selectedService === "clinical" ? "Participant search" : "Patient search" }
            subHeading={
            selectedService === "clinical" ? (
              "Enter all participant details below to search for their MyHealthChecked registration."
            ) : (
              <>
                Please ensure the patient has registered to MyHealthChecked.
                <br />
                Please enter 3 fields to search for a patient.
                <br />
                Please make sure the search details match the information provided by the patient when they registered with MyHealthChecked.
              </>
            )
          }
          formError={formError}
          >
            <SearchForm
              colStructure={6}
              fieldError={true}
              fields={fields}
              formError={getFormErrorMessage(selectedService)}
              onSubmit={handleSubmit}
              validationSchema={dateValidationSchema}
              serverError={updatedServerError}
              tokenError={tokenError}
              subtitle={"null"}
              title={"null"}
              onFormError={handleFormError}
              hideFormError={isVhlogin}
            />
            {showVhDuplicateTable && (
                <div className="patient-details-page multiple-user-page patient-table">
                  <div className="container">
                    <div className="row">
                      <div className="col-md-12 px-0">
                        <div className="page-layout__header p-0">
                          <h1>Search results found ({vhUsers.length})</h1>
                        </div>
                        <VHConfirmationPopup
                          warningText={
                            <span>
                              <b>Please verify the patient's details</b>
                              <br />
                              Multiple patients match the search criteria.
                              Before selecting a patient, carefully check that
                              their details match the information they provided
                              when registering with MyHealthChecked.
                            </span>
                          }
                          showIcon={false}
                        />
                        <VitalityDuplicatedTable
                          columns={vhColumns}
                          data={buildVhRows(vhUsers)}
                          onSubmit={handleSubmitDuplicateId}
                          pagination={true}
                          search={false}
                          sort={false}
                          rowKey="id"
                          statusLabel="View"
                          pageSize={4}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </PageLayout>
        ) : (
          <SearchForm
            colStructure={4}
            fieldError={true}
            fields={fields}
            formError={getFormErrorMessage(selectedService)}
            onSubmit={handleSubmit}
            validationSchema={dateValidationSchema}
            serverError={serverError}
            tokenError={tokenError}
            subtitle={
              selectedService === "healthCare"
                ? "Enter at least three exact matching details to search. Incorrect details or unregistered patients will return no results. Registered patients receive a Booking ID via email."
                : "Enter participant details to continue"
            }
            title={
              selectedService === "healthCare"
                ? "Patient search"
                : "Participant record"
            }
          />
        )}
      </>
    );
  };

  return (
    <LoggedInLayout
      buttonLabel={"Logout"}
      img={img}
      isActivation={false}
      links={links}
      logo={logo}
      pageTitle={auth.title}
      userInitials={auth.userInitials}
      isVhlogin={isVhlogin}
      vitalityLayout={isVhlogin}
    >
      {(() => {
        if (!serverError && resultPost && !showVhDuplicateTable) {
          return renderNavigationForResult(resultPost, data, selectedService);
        }

        return renderSearchForm({
          mfaProps: {
            mfa,
            show,
            handleMFA,
            handleHide,
          },
          searchFormProps: {
            fields,
            selectedService,
            handleSubmit,
            dateValidationSchema,
            serverError,
            tokenError,
          },
          toasterProps: {
            showError,
            toggleShowError,
          },
        });
      })()}
    </LoggedInLayout>
  );
}

export default PatientRecord;
